import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { NotificationBell } from "./NotificationBell";
import { LayoutDashboard, Building2, Users, HardHat, Wrench, LogOut, Menu, X } from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/properties", label: "Properties", icon: Building2 },
  { to: "/admin/homeowners", label: "Homeowners", icon: Users },
  { to: "/admin/contractors", label: "Contractors", icon: HardHat },
  { to: "/admin/service", label: "Service Requests", icon: Wrench },
];

export function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const Sidebar = (
    <div className="flex flex-col h-full bg-arq-sidebar text-arq-ink w-64">
      <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
        <img src="/arqitrak-logo.png" alt="ARQITRAK" className="h-5 w-auto" />
        <p className="text-[10px] uppercase tracking-widest text-arq-teal/70 border-l border-white/15 pl-2 ml-1">Admin</p>
      </div>
      <nav className="flex-1 p-3 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          return (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isActive ? "bg-arq-green text-white" : "text-white/70 hover:bg-arq-card/5 hover:text-white"}`}>
              <Icon className="h-[18px] w-[18px]" strokeWidth={1.6} />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        <div className="px-3 py-2">
          <p className="text-sm font-medium">{user?.name}</p>
          <p className="text-[11px] text-white/40">{user?.title || user?.role}</p>
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="admin-logout"
          className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-white/70 hover:bg-arq-card/5 w-full">
          <LogOut className="h-[18px] w-[18px]" strokeWidth={1.6} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-arq-bg flex">
      <aside className="hidden lg:block fixed inset-y-0 left-0 z-40">{Sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative z-10">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-30 bg-arq-card/80 backdrop-blur-xl border-b border-white/10 h-16 flex items-center justify-between px-4 sm:px-6">
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="admin-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <div className="hidden lg:block text-sm text-muted-foreground">Arqitek · Homeownership Platform</div>
          <div className="flex items-center gap-2">
            <NotificationBell />
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
